<template>
  <BaseMenu
    v-if="users"
    :items="usersList"
    :title="$t('go-nogo.invite')"
    :selection="usersSelected"
    :attach="attach"
    @add-item="addItemToSelection"
    @remove-item="removeItemFromSelection"
    @click-outside="applyImmediately"
    isMultiple
    goNogo
    ref="baseInvitationGoNoGo"
  >
    <template slot="activator">
      <BaseButton has-icon-left is-square>
        <BaseIcon name="plus" />
        {{ $t('go-nogo.invite') }}
      </BaseButton>
    </template>
    <template v-slot:default="{ item }">
      <div class="invite-go-nogo">
        <div class="invite-go-nogo__title">{{ item.name }}</div>
        <div class="invite-go-nogo__subtitle">{{ item.email }}</div>
      </div>
    </template>
  </BaseMenu>
</template>

<script>
import BaseIcon from '@/components/base/BaseIcon';
import BaseMenu from '@/components/base/BaseMenu';
import DebounceMixin from '@/mixins/debounce-mixin';

import { isAdminTenant } from '@/models/role';
import { mapActions, mapGetters } from 'vuex';
import BaseButton from '@/components/base/BaseButton.vue';

export default {
  name: 'BaseAssignation',
  components: {
    BaseButton,
    BaseMenu,
    BaseIcon,
  },
  mixins: [DebounceMixin],
  props: {
    value: {
      type: Array,
    },
    opportunityVotes: {
      type: Array,
      required: true,
    },
    opportunityId: {
      type: String,
      required: true,
    },
    tooltipPlacement: {
      type: String,
      required: false,
      default: 'top',
    },
    attach: {
      type: String,
    },
  },
  data() {
    return {
      innerValue: [],
    };
  },
  computed: {
    ...mapGetters('tender', ['tender']),
    ...mapGetters('auth', ['currentUser']),
    ...mapGetters('tenant', ['roleUserOnly', 'users']),
    usersList() {
      const userGroup = isAdminTenant(this.currentUser.role) ? this.users : this.roleUserOnly;
      return userGroup
        .map(({ id, email, firstName, lastName }) => ({
          id,
          email,
          name: this.nameRebuilder(firstName, lastName),
        }))
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    },
    usersSelected() {
      const mappedInnerValue = this.innerValue.map(({ userId, ...rest }) => ({
        id: userId,
        ...rest,
      }));

      this.opportunityVotes.forEach((opportunityVote) => {
        const { id, email, assignedDate, firstName, lastName } = opportunityVote.user;
        mappedInnerValue.push({
          id,
          email,
          assignedDate,
          firstName,
          lastName,
          voteId: opportunityVote.id,
          isPending: opportunityVote.isPending,
        });
      });

      return mappedInnerValue;
    },
  },
  methods: {
    ...mapActions('goNoGo', ['inviteUserOpportunityVote', 'deleteOpportunityVote']),
    isCurrentUser(assignee) {
      return assignee.userId === this.currentUser.id;
    },
    nameRebuilder(firstName, lastName) {
      return firstName + ' ' + lastName;
    },
    addItemToSelection(item) {
      const user = this.users.find((user) => user.id === item.id);
      const { id } = user;

      this.inviteUserOpportunityVote({ opportunityId: this.opportunityId, userId: id })
        .then((result) => {
          this.$toast.success(this.$t('go-nogo.invitation-success'));
        })
        .catch((error) => {
          this.$toast.error(error);
        });
    },
    removeItemFromSelection(item) {
      const relatedVote = this.usersSelected.find((userSelected) => userSelected.id === item.id);
      const { voteId, isPending } = relatedVote;

      if (!voteId) return;
      if (!isPending) {
        this.$toast.show(this.$t('go-nogo.ongoin-uninvitation-message'));
        return;
      }
      this.deleteOpportunityVote({ opportunityId: this.opportunityId, voteId })
        .then((result) => {
          this.$toast.success(this.$t('go-nogo.uninvitation-success'));
        })
        .catch((error) => {
          this.$toast.error(error);
        });
    },
    applyImmediately() {
      this.debounce(null, 0, true);
      this.applyChanges();
    },
    applyChanges() {
      const userIds = this.innerValue.map((user) => user.userId);
      this.$emit('change', userIds);
    },
    closeMenu() {
      this.$refs.baseInvitationGoNoGo.menu = false;
    },
  },
  watch: {
    value() {
      this.innerValue = [...this.value];
    },
  },
  created() {
    this.innerValue = [...this.value];
  },
  beforeDestroy() {
    this.innerValue = [];
  },
};
</script>

<style lang="scss">
.invite-go-nogo {
  display: flex;
  flex-direction: column;
  gap: 4px;

  &__title {
    font-size: 14px;
    color: $color-normal;
  }

  &__subtitle {
    font-size: 12px;
    color: $color-neutral;
  }
}
</style>
